import { type ChangeEvent, useState, useCallback, useRef } from 'react'
import { Navigate, useLocation } from 'react-router'
import { IntegrationTabs } from '@/ui/components/integration-components/integration-tabs'
import { IntegrationsList } from '@/ui/components/integration-components/integrations-list'
import {
  Base,
  MagnifyingGlassIcon,
  ReverLoading
} from '@itsrever/design-system'
import type { Variants } from 'framer-motion'
import { motion, useAnimation } from 'framer-motion'
import InstalledIntegrations from '@/ui/components/integration-components/installed-integrations'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import { NavigationTabs, StoreOnly, type TabProps } from '@/ui/shared'
import WebhooksSettings from '@/ui/components/integration-components/webhooks-settings'
import McpConnection from '@/ui/components/integration-components/mcp-connection'
import ApiKeys from '@/ui/pages/account/api-keys.page'
import IntegrationsOverview from '@/ui/components/integration-components/hub/integrations-overview'
import ConnectionsSection from '@/ui/components/integration-components/hub/connections-section'
import WarehousesSection from '@/ui/components/integration-components/hub/warehouses-section'
import CarriersSection from '@/ui/components/integration-components/hub/carriers-section'
import { BackToIntegrations } from '@/ui/components/integration-components/hub/hub-parts'
import {
  HubSection,
  hubSectionOf,
  INTEGRATIONS_PATH
} from '@/ui/components/integration-components/hub/hub-paths'
import {
  isIntegrationsStaffViewEnabled,
  isNangoCredentialsEnabled,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { useWarehousesScope } from '@/hooks/use-warehouses-scope'
import { useIsReverStaff } from '@/hooks/use-is-rever-staff'
import { useAuth0 } from '@auth0/auth0-react'

const WEBHOOKS_PATH = '/account/integrations/webhooks'
const API_KEYS_PATH = '/account/integrations/api-keys'
const MCP_PATH = '/account/integrations/mcp'
const CREDENTIALS_PATH = '/account/integrations/credentials'

const { Input, TabPanel, TabsRoot, TabsIndicator, TabsList, Tab } = Base

export const categories = [
  'All',
  'Fulfillment',
  'Shipping',
  'ERP',
  'Platforms',
  'Customer support',
  'Payments'
]
export type Category = (typeof categories)[number]

const IntegrationsPage = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const profile = useAppSelector((s) => s.userApi.getMe)
  // getMe is dispatched from an effect: deciding before it lands would show an admin the catalog.
  const loadingProfile =
    profile.loading === 'idle' || profile.loading === 'pending'
  const isAdmin = profile.response.user?.role === Roles.ReadWrite

  const onWebhooks = location.pathname.startsWith(WEBHOOKS_PATH)
  const onApiKeys = location.pathname.startsWith(API_KEYS_PATH)
  const onMcp = location.pathname.startsWith(MCP_PATH)
  const { scope } = useWarehousesScope()
  // As settings-api asks: a portal by its id and group, a group by its group alone. The settings
  // only follow the last store loaded, which may not be the one picked or even in its group.
  const { isFlagEnabled: hubEnabled, isFlagLoading: loadingHubFlag } =
    useFeatureFlag(
      isNangoCredentialsEnabled,
      scope.kind === 'none'
        ? {}
        : {
            group: scope.group,
            ...ecommerceContext(scope.kind === 'store' ? scope.ecommerceId : '')
          }
    )
  const isStaff = useIsReverStaff()
  const { user } = useAuth0()
  const { isFlagEnabled: staffView, isFlagLoading: loadingStaffFlag } =
    useFeatureFlag(
      isIntegrationsStaffViewEnabled,
      { email: user?.email?.toLowerCase() },
      { enabled: isStaff }
    )
  // Connecting credentials is a write: admins only, like webhooks and API keys.
  const canEdit = isAdmin && hubEnabled
  const showHub = canEdit || staffView
  const decidingHub =
    loadingProfile ||
    ((isAdmin || isStaff) && loadingHubFlag) ||
    (isStaff && loadingStaffFlag)
  const hubSection = hubSectionOf(location.pathname)

  const tabs: TabProps[] = [
    { link: INTEGRATIONS_PATH, copy: t('drawer_pages.integrations') }
  ]
  if (isAdmin) {
    tabs.push({ link: WEBHOOKS_PATH, copy: t('drawer_pages.webhooks') })
    tabs.push({ link: API_KEYS_PATH, copy: t('drawer_pages.api-keys') })
  }
  tabs.push({ link: MCP_PATH, copy: t('drawer_pages.mcp') })
  const selectedIndex = onWebhooks
    ? tabs.findIndex((tab) => tab.link === WEBHOOKS_PATH)
    : onApiKeys
      ? tabs.findIndex((tab) => tab.link === API_KEYS_PATH)
      : onMcp
        ? tabs.findIndex((tab) => tab.link === MCP_PATH)
        : 0

  // The credentials tab became the hub's sections, and those urls lead nowhere without the hub.
  const staleHubUrl =
    location.pathname.startsWith(CREDENTIALS_PATH) || (!showHub && !!hubSection)
  if (!decidingHub && staleHubUrl) {
    return <Navigate to={`${INTEGRATIONS_PATH}${location.search}`} replace />
  }

  return (
    <div className="flex flex-col overflow-y-hidden">
      <NavigationTabs tabs={tabs} selectedIndex={selectedIndex} />
      {onApiKeys ? (
        <ApiKeys />
      ) : onMcp ? (
        <McpConnection />
      ) : onWebhooks ? (
        <StoreOnly>
          <WebhooksSettings />
        </StoreOnly>
      ) : decidingHub ? (
        <ReverLoading />
      ) : showHub && hubSection ? (
        <HubSectionPage section={hubSection} readOnly={!canEdit} />
      ) : showHub ? (
        <IntegrationsOverview readOnly={!canEdit} />
      ) : (
        <StoreOnly>
          <AppStoreCatalog />
        </StoreOnly>
      )}
    </div>
  )
}

// Every spelling useFeatureFlag sends, so none keeps the last store loaded.
const ecommerceContext = (ecommerceId: string) => ({
  ecommerce_id: ecommerceId,
  ecommerceid: ecommerceId,
  ecommerceId
})

const HubSectionPage = ({
  section,
  readOnly
}: {
  section: HubSection
  readOnly: boolean
}) => (
  <>
    <BackToIntegrations />
    {section === 'platform' ? (
      <ConnectionsSection kind="platform" readOnly={readOnly} />
    ) : section === 'warehouses' ? (
      <WarehousesSection />
    ) : section === 'carriers' ? (
      <CarriersSection />
    ) : (
      <ConnectionsSection kind="app" readOnly={readOnly} />
    )}
  </>
)

const AppStoreCatalog = () => {
  const { t } = useTranslation()
  const [category, setCategory] = useState<Category>('All')
  const [searchQuery, setSearchQuery] = useState<string>('')

  return (
    <div className="min-h-full bg-neutral-100/10">
      <div className="min-h-full bg-neutral-100/10 px-5 pb-5 pt-4">
        <div className="flex items-center justify-between gap-3.5 border-b border-solid border-neutral-200 pb-4">
          <ConnectIcon />
          <header className="mr-auto space-y-1">
            <h1 className="font-[Roobert] text-lg font-medium leading-6 text-neutral-900">
              {t('integrations_store.integrations')}
            </h1>
            <h2 className="font-[Roobert] text-sm font-normal leading-5 text-neutral-600">
              {t('integrations_store.leverage_integrations')}
            </h2>
          </header>
        </div>

        <div className="container mx-auto mt-4 max-w-screen-xl">
          <TabsRoot defaultValue="explore">
            <TabsList>
              <Tab value="explore">{t('integrations_store.explore_tab')}</Tab>
              <Tab value="installed">
                {t('integrations_store.installed_tab')}
              </Tab>
              <TabsIndicator />
            </TabsList>
            <div className="mt-5 flex w-full items-center justify-between gap-4">
              <IntegrationTabs
                onCategoryChange={setCategory}
                activeCategory={category}
              />
              <Input
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setSearchQuery(e.target.value)
                }
                value={searchQuery}
                placeholder={t('integrations_store.search_bar')}
                className="ml-auto w-72"
                prefix={<MagnifyingGlassIcon className="size-5" />}
              />
            </div>
            <div className="mx-auto mt-2 flex  flex-col self-center ">
              <TabPanel value="explore">
                <IntegrationsList
                  category={category}
                  searchQuery={searchQuery}
                />
              </TabPanel>
              <TabPanel value="installed">
                <InstalledIntegrations
                  category={category}
                  searchQuery={searchQuery}
                />
              </TabPanel>
            </div>
          </TabsRoot>
        </div>
      </div>
    </div>
  )
}

export default IntegrationsPage

export interface ConnectIconHandle {
  startAnimation: () => void
  stopAnimation: () => void
}

const plugVariants: Variants = {
  normal: {
    x: 0,
    y: 0
  },
  animate: {
    x: -3,
    y: 3
  }
}

const socketVariants: Variants = {
  normal: {
    x: 0,
    y: 0
  },
  animate: {
    x: 3,
    y: -3
  }
}

const pathVariants = {
  normal: (custom: { x: number; y: number }) => ({
    d: `M${custom.x} ${custom.y} l2.5 -2.5`
  }),
  animate: (custom: { x: number; y: number }) => ({
    d: `M${custom.x + 2.93} ${custom.y - 2.93} l0.10 -0.10`
  })
}

const ConnectIcon = () => {
  const controls = useAnimation()
  const isControlledRef = useRef(false)

  const handleMouseEnter = useCallback(() => {
    if (!isControlledRef.current) {
      controls.start('animate')
    }
  }, [controls])

  const handleMouseLeave = useCallback(() => {
    if (!isControlledRef.current) {
      controls.start('normal')
    }
  }, [controls])
  return (
    <div
      className="hover:bg-accent flex select-none items-center justify-center rounded-full border border-solid border-neutral-200 bg-white p-3 shadow-sm transition-colors duration-200"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={22}
        height={22}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <motion.path
          d="M19 5l3 -3"
          variants={{
            normal: {
              d: 'M19 5l3 -3'
            },
            animate: {
              d: 'M17 7l5 -5'
            }
          }}
          animate={controls}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }}
        />
        <motion.path
          d="m2 22 3-3"
          variants={{
            normal: {
              d: 'm2 22 3-3'
            },
            animate: {
              d: 'm2 22 6-6'
            }
          }}
          animate={controls}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }}
        />
        <motion.path
          d="M6.3 20.3a2.4 2.4 0 0 0 3.4 0L12 18l-6-6-2.3 2.3a2.4 2.4 0 0 0 0 3.4Z"
          variants={socketVariants}
          animate={controls}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }}
        />
        <motion.path
          variants={pathVariants}
          custom={{ x: 7.5, y: 13.5 }}
          initial="normal"
          animate={controls}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }}
        />
        <motion.path
          variants={pathVariants}
          custom={{ x: 10.5, y: 16.5 }}
          initial="normal"
          animate={controls}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }}
        />
        <motion.path
          d="m12 6 6 6 2.3-2.3a2.4 2.4 0 0 0 0-3.4l-2.6-2.6a2.4 2.4 0 0 0-3.4 0Z"
          variants={plugVariants}
          animate={controls}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }}
        />
      </svg>
    </div>
  )
}
